import { zodResolver } from "@hookform/resolvers/zod";
import { useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import dayjs from "dayjs";
import { sum, sumBy } from "lodash";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useFieldArray, useForm, UseFormReturn } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { z } from "zod";

import { components } from "@/@types/api";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { HIDE_DECIMAL, useConfig } from "@/hooks/useConfig";
import { ScenarioTypeEnum } from "@/pages/scenario-builder/types";
import { isDefined } from "@/Utils/array-utils";

import { BrandGeographiesDTO, useModelMetadata } from "../../api/getCustomerModelMetadata";
import { useDateRanges } from "../../api/getDateRanges";
import { HistoricalSpendDTO, useHistoricalSpends } from "../../api/getHistoricalSpend";
import { Scenario } from "../../api/getScenario";
import { useRunScenario } from "../../api/runScenario";
import { SpendGeographicGranularity, SpendGeographicGranularityTypes } from "../../api/types";
import { useUpdateScenarioV3 } from "../../api/updateScenario";
import { confirmResetScenarioConstraints } from "../../components/ResetScenarioConstraintsConfirmation";
import { useGeoEnabled } from "../../hooks/useGeoEnabled";
import { useScenariosV3Routes } from "../../hooks/useScenariosV3Routes";
import { useScenarioV2 } from "../../hooks/useScenarioV2";
import { bcpLayerKey, BCPNode } from "../../utils";
import { useScenarioAnalyticsV3 } from "../../utils/useScenarioAnalyticsV3";
import { MediaConstraint, ScenarioDateRange, SpendInput } from "./types";
import {
  END_DATE_ERRORS,
  MIN_SCENARIO_DURATION_IN_DAYS,
  NAME_ERRORS,
  START_DATE_ERRORS,
  TOTAL_BUDGET_ERRORS,
} from "./utils/constants";
import {
  getAllowedMaxSpendForConstraint,
  minSpendGreaterThanMax,
  totalConstraintsExceedTotalBudget,
} from "./utils/formValidationRules";
import {
  allocateForecastBudget,
  filterHistoricalSpendsForValidation,
  getHistoricalSpends,
  HistoricalSpends,
  roundDownToNearestThousand,
} from "./utils/logic";
import { SpendConstraints, useSpendConstraints } from "../../api/v2/getSpendConstraints";
import { ScenarioType, SpendConstraintsResponse } from "@mutiny-group/sbo-openapi-client";
import { useChangeScenarioType } from "../../api/v2/changeScenarioType";

export type ScenarioInformationFormData = {
  uuid: string;
  name: string;
  description?: string | null;
  scenarioTarget?: string | null;
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  type: string;
  geography: string | null;
  spendGeographicGranularity: SpendGeographicGranularity | null;
};

export type BudgetAllocationFormData = {
  totalBudget: number | null;
};

export type MediaConstraintsFormData = {
  mediaConstraints: MediaConstraint[];
};

export type SpendInputFormData = {
  spendInputs: SpendInput[];
};

export type ForecastErrors = {
  allocation: {
    constraints_exceed_total_budget: boolean;
  };
};

export type OptimizerErrors = {
  allocation: {
    constraints_exceed_total_budget: boolean;
  };
};

type RunScenarioRequest = components["schemas"]["RunScenarioRequestV2"];

export const ConstraintsMaxTotalExceedsTotalBudgetErrorKey = "constraints_exceed_total_budget";
export const ConstraintsMinTotalExceedsTotalBudgetErrorKey =
  "constraints_min_total_exceeds_total_budget";

export type FormGeographyStatus =
  | {
      type: "disabled";
    }
  | {
      type: "single_geography";
      geography: string;
    }
  | {
      type: "all_geographies";
      spendGeographicGranularity: SpendGeographicGranularity;
    };

const ScenarioFormContext = createContext<{
  scenarioInformationForm: UseFormReturn<ScenarioInformationFormData>;
  geoStatus: FormGeographyStatus;
  handleScenarioInformationChange: (
    fieldName: keyof ScenarioInformationFormData,
    value: string | null | dayjs.Dayjs
  ) => Promise<void>;
  handleSpendGeographicGranularityChange: (
    value: "all_geographies" | "single_geography" | null
  ) => Promise<void>;
  budgetAllocationForm: UseFormReturn<BudgetAllocationFormData>;
  handleTotalBudgetChange: (
    fieldName: keyof BudgetAllocationFormData,
    value: number | null
  ) => Promise<void>;
  mediaConstraintsForm: UseFormReturn<MediaConstraintsFormData>;
  handleMediaConstraintsRowChange: (
    action:
      | {
          type: "add";
          value: MediaConstraint;
        }
      | {
          type: "remove";
          index: number;
        }
      | {
          type: "update";
          index: number;
          value: MediaConstraint;
        }
  ) => Promise<void>;
  handleRemoveMediaConstraintsBusinessUnit: (bcp: BCPNode) => Promise<void>;
  spendInputForm: UseFormReturn<SpendInputFormData>;
  handleSpendInputRowChange: (
    action:
      | {
          type: "add";
          value: SpendInput;
        }
      | {
          type: "remove";
          index: number;
        }
      | {
          type: "update";
          index: number;
          value: SpendInput;
        }
  ) => Promise<void>;
  handleRemoveSpendInputBusinessUnit: (bcp: BCPNode) => Promise<void>;
  handleResetConstraints: () => Promise<void>;
  brandGeographies: BrandGeographiesDTO[] | undefined;
  scenario: Scenario | undefined;
  allowedDateRange: ScenarioDateRange | undefined;
  historicalSpends: HistoricalSpends | undefined;
  historicalSpendsDTO: HistoricalSpendDTO[] | undefined;
  maxAllowedTotalBudget: number | undefined;
  loading: boolean;
  areHistoricalSpendsLoading: boolean;
  budgetAllocation: SpendInput[] | undefined;
  saveForm: () => Promise<void>;
  handleRunScenario: () => Promise<void>;
  isSaving: boolean;
  validateForms: () => Promise<boolean>;
  isSubmitting: boolean;
  distinctBCP: Map<string, BCPNode> | undefined;
  spendConstraintsDTO: SpendConstraints | undefined;
  isSpendConstraintsLoading: boolean;
} | null>(null);

export const ScenarioFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config } = useConfig();
  const { uuid } = useParams<{ uuid: string }>();
  const theme = useTheme();
  const formatCurrency = config.currency.formatCurrency;

  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);
  const { value: isScenarioBuilderConstraintsFromApiEnabled } = useFeatureGate(
    featureFlag.scenarioBuilderConstraintsFromApi
  );

  const { scenario, isScenarioLoading } = useScenarioV2({
    uuid,
    isScenariosApiEnabled,
  });
  const scenarioType = scenario?.scenarioType;

  // Initialize analytics tracking for v3 scenario form
  const { trackScenarioUpdated, trackScenarioSubmitted, trackScenarioFormError } =
    useScenarioAnalyticsV3({
      scenario,
    });

  // Track if scenario has been initialized for analytics
  const scenarioInitializedRef = useRef(false);

  const { data: metaData, isLoading: isBrandGeographiesLoading } = useModelMetadata({
    config: {
      refetchOnWindowFocus: false,
    },
    isScenariosApiEnabled,
  });

  const dayjsSchema = z
    .any()
    .refine((val) => dayjs.isDayjs(val), {
      message: "Must be a valid dayjs object",
    })
    .transform((val) => dayjs(val));

  const scenarioInformationSchema = z
    .object({
      uuid: z.string(),
      name: z.string().min(1, NAME_ERRORS.REQUIRED).max(128, NAME_ERRORS.MAX_LENGTH),
      description: z.string().optional().nullable(),
      scenarioTarget: z.string().optional().nullable(),
      scenarioStatus: z.string().optional().nullable(),
      startDate: dayjsSchema
        .pipe(
          z.custom<dayjs.Dayjs>().refine((val) => val.isValid(), {
            message: START_DATE_ERRORS.REQUIRED,
          })
        )
        .optional(),
      endDate: dayjsSchema
        .pipe(
          z.custom<dayjs.Dayjs>().refine((val) => val.isValid(), {
            message: END_DATE_ERRORS.REQUIRED,
          })
        )
        .optional(),
      type: z.string(),
      geography: z.string().nullable(),
      spendGeographicGranularity: z.enum(SpendGeographicGranularityTypes).nullable(),
    })
    .superRefine((data, ctx) => {
      if (!data.startDate || !data.endDate) return;
      if (data.endDate.diff(data.startDate, "day") < MIN_SCENARIO_DURATION_IN_DAYS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End Date must be at least 28 days after the Start Date.",
          path: ["endDate"],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (!data.startDate || !dateRanges?.startDate) return;
      if (dayjs(data.startDate).isBefore(dayjs(dateRanges.startDate), "day")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start Date must be after the earliest date in the date range.",
          path: ["startDate"],
        });
      }
    });

  type ScenarioInformationFormData = z.infer<typeof scenarioInformationSchema>;

  const scenarioInformationForm = useForm<ScenarioInformationFormData>({
    resolver: zodResolver(scenarioInformationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      geography: null,
      spendGeographicGranularity: null,
    },
  });

  const geoEnabled = useGeoEnabled();
  const { startDate, endDate, geography, scenarioTarget, spendGeographicGranularity } =
    scenarioInformationForm.watch();

  const { data: dateRanges } = useDateRanges({
    geography: geography ?? undefined,
    isScenariosApiEnabled,
  });

  const geoStatus = useMemo<FormGeographyStatus>(() => {
    if (geoEnabled.isLoading || !geoEnabled.isEnabled) {
      return {
        type: "disabled" as const,
      };
    }

    if (isDefined(geography)) {
      return {
        type: "single_geography" as const,
        geography,
      };
    }

    return {
      type: "all_geographies" as const,
      spendGeographicGranularity: spendGeographicGranularity ?? "all_geographies",
    };
  }, [geoEnabled, geography, spendGeographicGranularity]);

  const scopeGeographies = useMemo(() => {
    if (geoStatus.type === "single_geography") {
      return [geoStatus.geography];
    }

    if (spendGeographicGranularity === "single_geography" && scenarioTarget) {
      return metaData?.brand_geographies.find(
        (brandGeography) => brandGeography.brand === scenarioTarget
      )?.geographies;
    }

    return;
  }, [geoStatus, spendGeographicGranularity, metaData, scenarioTarget]);

  const { data: historicalSpendsDTO, isRefetching: areHistoricalSpendsLoading } =
    useHistoricalSpends({
      config: {
        enabled: !!startDate && !!endDate,
        refetchOnWindowFocus: false,
      },
      startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
      brand: scenarioTarget ?? undefined,
      geographies: scopeGeographies ?? undefined,
      isScenariosApiEnabled,
    });

  const { data: spendConstraintsDTO, isLoading: isSpendConstraintsLoading } = useSpendConstraints({
    startDate: scenarioInformationForm?.getValues("startDate")?.format("YYYY-MM-DD") ?? null,
    endDate: scenarioInformationForm?.getValues("endDate")?.format("YYYY-MM-DD") ?? null,
    scenarioType: scenarioType as ScenarioType,
    brand: scenarioInformationForm?.getValues("scenarioTarget") ?? null,
    geography: scenarioInformationForm?.getValues("geography") ?? null,
    groupBy: ["brand", "category", "product", "geography"],
    config: {
      enabled:
        isScenarioBuilderConstraintsFromApiEnabled &&
        !!scenarioInformationForm?.getValues("startDate") &&
        !!scenarioInformationForm?.getValues("endDate"),
    },
  });

  const maxAllowedTotalBudget = useMemo(() => {
    if (isScenarioBuilderConstraintsFromApiEnabled) {
      return spendConstraintsDTO?.maxTotalBudget ?? undefined;
    }

    if (!historicalSpendsDTO) return;

    // Filter to match external service validation (single geography + brand scope)
    const filteredSpends = filterHistoricalSpendsForValidation(historicalSpendsDTO, {
      brand: scenarioTarget,
      geography: geography,
    });

    const totalHistoricalSpend = filteredSpends.reduce(
      (acc, spend) => acc + spend.marketing_spend.spend,
      0
    );

    return roundDownToNearestThousand(totalHistoricalSpend * 2);
  }, [
    historicalSpendsDTO,
    geography,
    scenarioTarget,
    spendConstraintsDTO,
    isScenarioBuilderConstraintsFromApiEnabled,
  ]);

  const historicalSpends = useMemo(() => {
    if (!scenarioTarget || !historicalSpendsDTO) return;

    return getHistoricalSpends(historicalSpendsDTO, scenarioTarget);
  }, [scenarioTarget, historicalSpendsDTO]);

  const budgetAllocationSchema = isDefined(maxAllowedTotalBudget)
    ? z.object({
        totalBudget: z
          .number({
            required_error: TOTAL_BUDGET_ERRORS.REQUIRED,
            invalid_type_error: "Total Budget must be a number",
          })
          .positive(TOTAL_BUDGET_ERRORS.POSITIVE)
          .int(TOTAL_BUDGET_ERRORS.INTEGER)
          .min(
            0,
            TOTAL_BUDGET_ERRORS.GET_MIN_MAX_MESSAGE(
              formatCurrency(0),
              formatCurrency(maxAllowedTotalBudget)
            )
          )
          .max(
            maxAllowedTotalBudget,
            TOTAL_BUDGET_ERRORS.GET_MIN_MAX_MESSAGE(
              formatCurrency(0),
              formatCurrency(maxAllowedTotalBudget)
            )
          )
          .nullable()
          .refine((val) => val !== null, {
            message: TOTAL_BUDGET_ERRORS.REQUIRED,
          }),
      })
    : z.object({
        totalBudget: z.number().nullable(),
      });

  type BudgetAllocationFormData = z.input<typeof budgetAllocationSchema>;

  const budgetAllocationForm = useForm<BudgetAllocationFormData>({
    resolver: zodResolver(budgetAllocationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  // Optimizer constraints schema
  const mediaConstraintsSchema = z
    .object({
      mediaConstraints: z.array(
        z
          .object({
            channel: z.string().min(1, "Media Channel is Required"),
            brand: z.string().min(1, "Business Unit is Required"),
            category: z.string().min(1, "Business Unit is Required"),
            product: z.string().min(1, "Business Unit is Required"),
            minValue: z.number({
              required_error: "Minimum Spend is Required",
              invalid_type_error: "Minimum Spend must be a number",
            }),
            maxValue: z.number({
              required_error: "Maximum Spend is Required",
              invalid_type_error: "Maximum Spend must be a number",
            }),
            geography: z.string().min(1, "Geography is required").nullable().optional(),
          })
          .superRefine((constraint, ctx) => {
            if (minSpendGreaterThanMax(constraint)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Must be less than or equal to Max Spend",
                path: ["minValue"],
              });
            }
          })
          .superRefine((constraint, ctx) => {
            if (!isScenarioBuilderConstraintsFromApiEnabled && !historicalSpendsDTO) return;

            const maxSpend = getAllowedMaxSpendForConstraint(
              {
                brand: constraint.brand,
                category: constraint.category,
                product: constraint.product,
                channel: constraint.channel,
                geography: constraint.geography ?? null,
              },
              historicalSpendsDTO,
              spendConstraintsDTO,
              isScenarioBuilderConstraintsFromApiEnabled
            );

            if (!maxSpend) return;

            if (constraint.maxValue > maxSpend) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Value exceeds max spend: ${formatCurrency(maxSpend, HIDE_DECIMAL)}`,
                path: ["maxValue"],
              });
            }
          })
          .superRefine((constraint, ctx) => {
            if (historicalSpendsDTO) {
              const matchingHistoricalSpend = historicalSpendsDTO.find(
                (dto) =>
                  dto.marketing_spend.marketing_spend_node.brand === constraint.brand &&
                  dto.marketing_spend.marketing_spend_node.category === constraint.category &&
                  dto.marketing_spend.marketing_spend_node.product === constraint.product &&
                  dto.marketing_spend.channel === constraint.channel &&
                  dto.marketing_spend.marketing_spend_node.geography ===
                    (constraint.geography ?? null)
              );

              if (matchingHistoricalSpend && !matchingHistoricalSpend.is_node_channel_active) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message:
                    "This media channel is not active for the selected business unit and geography",
                  path: ["channel"],
                });
              }
            }
          })
      ),
    })
    // No max constraints limit for Optimizer, only min constraints can't exceed total budget
    .superRefine((constraints, ctx) => {
      if (!isDefined(watchedTotalBudget)) return;

      const totalMinConstraints = sumBy(constraints.mediaConstraints, "minValue") ?? 0;

      if (totalConstraintsExceedTotalBudget(totalMinConstraints, watchedTotalBudget)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Total min constraints exceeds total budget`,
          path: ["root", ConstraintsMinTotalExceedsTotalBudgetErrorKey],
        });
      }
    });

  type MediaConstraintsFormData = z.infer<typeof mediaConstraintsSchema>;

  const mediaConstraintsForm = useForm<MediaConstraintsFormData>({
    resolver: zodResolver(mediaConstraintsSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    criteriaMode: "all",
  });

  // Forecast (budget) constraints schema
  const spendInputSchema = z
    .object({
      spendInputs: z.array(
        z
          .object({
            channel: z.string().min(1, "Media Channel is Required"),
            brand: z.string().min(1, "Business Unit is Required"),
            category: z.string().min(1, "Business Unit is Required"),
            product: z.string().min(1, "Business Unit is Required"),
            spend: z.number({
              required_error: "Spend is Required",
              invalid_type_error: "Spend must be a number",
            }),
            geography: z.string().min(1, "Geography is required").nullable().optional(),
          })
          .superRefine((constraint, ctx) => {
            if (!isScenarioBuilderConstraintsFromApiEnabled && !historicalSpendsDTO) return;

            const maxSpend = getAllowedMaxSpendForConstraint(
              {
                brand: constraint.brand,
                category: constraint.category,
                product: constraint.product,
                channel: constraint.channel,
                geography: constraint.geography ?? null,
              },
              historicalSpendsDTO,
              spendConstraintsDTO,
              isScenarioBuilderConstraintsFromApiEnabled
            );

            if (!maxSpend) return;

            if (constraint.spend > maxSpend) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Spend exceeds max spend: ${formatCurrency(maxSpend, HIDE_DECIMAL)}`,
                path: ["spend"],
              });
            }
          })
          .superRefine((constraint, ctx) => {
            if (historicalSpendsDTO) {
              const matchingHistoricalSpend = historicalSpendsDTO.find(
                (dto) =>
                  dto.marketing_spend.marketing_spend_node.brand === constraint.brand &&
                  dto.marketing_spend.marketing_spend_node.category === constraint.category &&
                  dto.marketing_spend.marketing_spend_node.product === constraint.product &&
                  dto.marketing_spend.channel === constraint.channel &&
                  dto.marketing_spend.marketing_spend_node.geography ===
                    (constraint.geography ?? null)
              );

              if (matchingHistoricalSpend && !matchingHistoricalSpend.is_node_channel_active) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message:
                    "This media channel is not active for the selected business unit and geography",
                  path: ["channel"],
                });
              }
            }
          })
      ),
    })
    .superRefine((constraints, ctx) => {
      const totalConstraints = sum(
        constraints.spendInputs.map((constraint) => constraint.spend ?? 0)
      );

      if (!isDefined(watchedTotalBudget)) {
        return;
      }

      if (totalConstraintsExceedTotalBudget(totalConstraints, watchedTotalBudget)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Total constraints exceeds total budget`,
          path: ["root", ConstraintsMaxTotalExceedsTotalBudgetErrorKey],
        });
      }
    });

  type SpendInputFormData = z.infer<typeof spendInputSchema>;

  const spendInputForm = useForm<SpendInputFormData>({
    resolver: zodResolver(spendInputSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    criteriaMode: "all",
  });

  const syncForm = useCallback(
    (scenario: Scenario) => {
      if (scenario?.uuid) {
        scenarioInformationForm.setValue("uuid", scenario?.uuid || "", { shouldValidate: true });
        scenarioInformationForm.setValue("name", scenario?.name || "", { shouldValidate: true });
        scenarioInformationForm.setValue("description", scenario?.description || "", {
          shouldValidate: true,
        });
        scenarioInformationForm.setValue("scenarioTarget", scenario?.brand, {
          shouldValidate: true,
        });
        scenarioInformationForm.setValue(
          "startDate",
          scenario?.startDate ? dayjs.utc(scenario?.startDate) : undefined,
          { shouldValidate: true }
        );
        scenarioInformationForm.setValue(
          "endDate",
          scenario?.endDate ? dayjs.utc(scenario?.endDate) : undefined,
          { shouldValidate: true }
        );
        scenarioInformationForm.setValue("type", scenario?.scenarioType || "", {
          shouldValidate: true,
        });
        scenarioInformationForm.setValue("geography", scenario?.geography ?? null, {
          shouldValidate: true,
        });

        // Only set spendGeographicGranularity from API if not already set locally
        // since the API no longer persists this value after migration
        const currentGranularity = scenarioInformationForm.getValues("spendGeographicGranularity");
        if (currentGranularity === null || currentGranularity === undefined) {
          scenarioInformationForm.setValue(
            "spendGeographicGranularity",
            scenario?.spendGeographicGranularity ?? null
          );
        }

        budgetAllocationForm.setValue("totalBudget", scenario?.value || null, {
          shouldValidate: true,
        });

        if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
          const constraints =
            scenario?.scenarioConstraints?.map((mediaConstraint) => ({
              channel: mediaConstraint.channel,
              brand: mediaConstraint.brand ?? "",
              category: mediaConstraint.category ?? "",
              product: mediaConstraint.product ?? "",
              minValue: mediaConstraint.minValue ?? 0,
              maxValue: mediaConstraint.maxValue ?? 0,
              geography: mediaConstraint.geography,
            })) ?? [];

          mediaConstraintsForm.setValue("mediaConstraints", constraints, {
            shouldValidate: true,
          });

          mediaConstraintsForm.reset({
            mediaConstraints: constraints,
          });
        }

        if (scenario?.scenarioType !== ScenarioTypeEnum.Optimizer) {
          const constraints =
            scenario?.scenarioConstraints?.map((mediaConstraint) => ({
              channel: mediaConstraint.channel,
              brand: mediaConstraint.brand ?? "",
              category: mediaConstraint.category ?? "",
              product: mediaConstraint.product ?? "",
              spend: mediaConstraint.maxValue ?? 0,
              geography: mediaConstraint.geography,
            })) ?? [];

          spendInputForm.setValue("spendInputs", constraints, {
            shouldValidate: true,
          });

          spendInputForm.reset({
            spendInputs: spendInputForm.getValues("spendInputs"),
          });
        }

        scenarioInformationForm.trigger();
        budgetAllocationForm.trigger();
        mediaConstraintsForm.trigger();
        spendInputForm.trigger();
      }
    },
    [scenarioInformationForm, budgetAllocationForm, mediaConstraintsForm, spendInputForm]
  );

  // Initialize forms with scenario data
  useEffect(() => {
    if (scenario?.uuid) {
      syncForm(scenario);
    }
  }, [scenario?.uuid, syncForm]);

  // Re-trigger validation when historicalSpends becomes available
  useEffect(() => {
    if (historicalSpends && scenario?.uuid) {
      // Re-trigger validation with the updated schemas
      scenarioInformationForm.trigger();
      budgetAllocationForm.trigger();
      mediaConstraintsForm.trigger();
      spendInputForm.trigger();
    }
  }, [
    historicalSpends,
    scenario?.uuid,
    scenarioInformationForm,
    budgetAllocationForm,
    mediaConstraintsForm,
    spendInputForm,
  ]);
  // Track scenario initialization (for form state management only)
  useEffect(() => {
    if (scenario?.uuid && !scenarioInitializedRef.current) {
      scenarioInitializedRef.current = true;
    }
  }, [scenario?.uuid]);

  const watchedTotalBudget = budgetAllocationForm.watch("totalBudget");
  const watchedSpendInputs = spendInputForm.watch("spendInputs");

  const budgetAllocation = useMemo(() => {
    return allocateForecastBudget(
      historicalSpendsDTO ?? [],
      watchedTotalBudget ?? 0,
      watchedSpendInputs ?? []
    );
  }, [historicalSpendsDTO, watchedTotalBudget, watchedSpendInputs]);

  const updateScenario = useUpdateScenarioV3({ isScenariosApiEnabled });
  const isSaving = updateScenario.isPending;

  const changeScenarioType = useChangeScenarioType();

  const validateForms = async (): Promise<boolean> => {
    const validationPromises = [scenarioInformationForm.trigger(), budgetAllocationForm.trigger()];

    // Add scenario-specific form validation
    if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
      validationPromises.push(mediaConstraintsForm.trigger());
    } else if (scenario?.scenarioType === ScenarioTypeEnum.Budget) {
      validationPromises.push(spendInputForm.trigger());
    }

    const results = await Promise.all(validationPromises);
    const isValid = results.every((result) => result);

    // Track validation errors when forms fail validation
    if (!isValid) {
      const errors = [];

      // Check scenario information errors
      const scenarioErrors = scenarioInformationForm.formState.errors;
      if (Object.keys(scenarioErrors).length > 0) {
        errors.push(`Scenario Information - ${Object.keys(scenarioErrors).join(", ")}`);
      }

      // Check budget allocation errors
      const budgetErrors = budgetAllocationForm.formState.errors;
      if (Object.keys(budgetErrors).length > 0) {
        errors.push(`Budget Allocation - ${Object.keys(budgetErrors).join(", ")}`);
      }

      // Check media constraints errors for optimizer
      if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
        const constraintErrors = mediaConstraintsForm.formState.errors;
        if (Object.keys(constraintErrors).length > 0) {
          errors.push(`Media Constraints - ${Object.keys(constraintErrors).join(", ")}`);
        }
      }

      // Check spend inputs errors for budget
      if (scenario?.scenarioType === ScenarioTypeEnum.Budget) {
        const spendErrors = spendInputForm.formState.errors;
        if (Object.keys(spendErrors).length > 0) {
          errors.push(`Spend Inputs - ${Object.keys(spendErrors).join(", ")}`);
        }
      }

      const errorMessage = errors.length > 0 ? errors.join(", ") : "Form validation failed";
      trackScenarioFormError(errorMessage);
    }

    return isValid;
  };

  const formsToScenario = () => {
    const scenarioUuid = scenario?.uuid;
    if (!isDefined(scenarioUuid)) {
      throw new Error("Scenario UUID is missing");
    }

    const scenarioConstraints =
      scenario?.scenarioType === ScenarioTypeEnum.Optimizer
        ? mediaConstraintsForm.getValues("mediaConstraints").map((constraint) => ({
            scenarioUuid,
            brand: constraint.brand,
            category: constraint.category,
            product: constraint.product,
            channel: constraint.channel,
            minValue: constraint.minValue,
            maxValue: constraint.maxValue,
            geography: constraint.geography,
          }))
        : spendInputForm.getValues("spendInputs").map((constraint) => ({
            scenarioUuid,
            brand: constraint.brand,
            category: constraint.category,
            product: constraint.product,
            channel: constraint.channel,
            minValue: null,
            maxValue: constraint.spend,
            geography: constraint.geography,
          }));

    return {
      name: scenarioInformationForm.getValues("name"),
      description: scenarioInformationForm.getValues("description"),
      brand: scenarioInformationForm.getValues("scenarioTarget"),
      geography: scenarioInformationForm.getValues("geography"),
      startDate: scenarioInformationForm.getValues("startDate")?.toISOString(),
      endDate: scenarioInformationForm.getValues("endDate")?.toISOString(),
      value: budgetAllocationForm.getValues("totalBudget"),
      scenarioConstraints,
    };
  };

  const handleScenarioInformationChange = async (
    fieldName: keyof ScenarioInformationFormData,
    value: string | null | dayjs.Dayjs
  ) => {
    // If we change the scope, we prompt to reset constraints
    if (["scenarioTarget", "geography", "type"].includes(fieldName)) {
      const hasSetConstraints =
        spendInputForm.getValues("spendInputs").length > 0 ||
        mediaConstraintsForm.getValues("mediaConstraints").length > 0;

      // Only confirm if the user has set constraints
      if (hasSetConstraints) {
        const subtitle =
          fieldName === "type"
            ? "Changing the scenario type will reset all existing constraints. This cannot be undone."
            : undefined;

        const confirmed = await confirmResetScenarioConstraints({ theme, subtitle });

        if (!confirmed) {
          return;
        }
      }

      spendInputForm.reset({
        spendInputs: [],
      });
      mediaConstraintsForm.reset({
        mediaConstraints: [],
      });

      scenarioInformationForm.setValue("spendGeographicGranularity", null);
    }

    scenarioInformationForm.setValue(fieldName, value);

    // Check for scenario information validation errors after setting value
    await scenarioInformationForm.trigger();
    const scenarioErrors = scenarioInformationForm.formState.errors;
    if (Object.keys(scenarioErrors).length > 0) {
      const errorFields = Object.keys(scenarioErrors).join(", ");
      trackScenarioFormError(`Scenario information validation error - ${errorFields}`);
    }

    // If scenario type is being changed, make a post request to the change scenario type endpoint, otherwise update the scenario
    if (fieldName === "type") {
      await changeScenarioType.mutateAsync({
        scenarioUUID: uuid,
        scenarioType: value as ScenarioType,
      });
    } else {
      const scenario = formsToScenario();
      await updateScenario.mutateAsync({
        scenarioUUID: uuid,
        scenario,
        scenarioType,
      });
    }

    // Track scenario update
    trackScenarioUpdated();

    await budgetAllocationForm.trigger();
  };

  const handleSpendGeographicGranularityChange = async (
    value: SpendGeographicGranularity | null
  ) => {
    spendInputForm.reset({
      spendInputs: [],
    });
    mediaConstraintsForm.reset({
      mediaConstraints: [],
    });

    // Update form value locally - this is now client-side only
    // since the API no longer supports spendGeographicGranularity
    scenarioInformationForm.setValue("spendGeographicGranularity", value);

    // Still need to save the constraints reset to the API
    const scenario = formsToScenario();

    const updatedScenario = await updateScenario.mutateAsync({
      scenarioUUID: uuid,
      scenario,
      scenarioType,
    });

    syncForm(updatedScenario);
  };

  const handleTotalBudgetChange = async (
    fieldName: keyof BudgetAllocationFormData,
    value: number | null
  ) => {
    budgetAllocationForm.setValue(fieldName, value!, { shouldValidate: false, shouldTouch: true });

    const scenario = formsToScenario();

    await updateScenario.mutateAsync({
      scenarioUUID: uuid,
      scenario,
      scenarioType,
    });

    // Only trigger validation on other forms if the budget field is valid and has been touched
    // This prevents cascade validation on forms when user is still typing
    const isBudgetFieldTouched = budgetAllocationForm.formState.touchedFields[fieldName];
    if (isBudgetFieldTouched) {
      await mediaConstraintsForm.trigger();
      await spendInputForm.trigger();
    }
  };

  const mediaConstraintsFields = useFieldArray({
    control: mediaConstraintsForm.control,
    name: "mediaConstraints",
  });

  const handleMediaConstraintsRowChange = async (
    action:
      | {
          type: "add";
          value: MediaConstraint;
        }
      | {
          type: "remove";
          index: number;
        }
      | {
          type: "update";
          index: number;
          value: MediaConstraint;
        }
  ) => {
    if (action.type === "add") {
      mediaConstraintsFields.append(action.value);
      // Don't trigger validation for new items - let them validate onBlur or on save
      return;
    } else if (action.type === "remove") {
      mediaConstraintsFields.remove(action.index);
    } else if (action.type === "update") {
      mediaConstraintsFields.update(action.index, action.value);
    }

    // Let the form handle validation naturally through onBlur behavior
    // Only trigger validation on save/submit, not on programmatic updates

    // Reset the form to un-dirty
    // mediaConstraintsForm.reset();
  };

  const handleRemoveMediaConstraintsBusinessUnit = async (bcp: BCPNode) => {
    const rows = mediaConstraintsForm.getValues("mediaConstraints");
    const newRows = rows.filter(
      (row) =>
        row.brand !== bcp.brand || row.category !== bcp.category || row.product !== bcp.product
    );
    mediaConstraintsForm.setValue("mediaConstraints", newRows);

    const scenario = formsToScenario();

    await updateScenario.mutateAsync({
      scenarioUUID: uuid,
      scenario,
      scenarioType,
    });

    await mediaConstraintsForm.trigger();

    // Reset the form to un-dirty
    mediaConstraintsForm.reset({
      mediaConstraints: newRows,
    });
  };

  const spendInputFields = useFieldArray({
    control: spendInputForm.control,
    name: "spendInputs",
  });

  const handleSpendInputRowChange = async (
    action:
      | {
          type: "add";
          value: SpendInput;
        }
      | {
          type: "remove";
          index: number;
        }
      | {
          type: "update";
          index: number;
          value: SpendInput;
        }
  ) => {
    if (action.type === "add") {
      spendInputFields.append(action.value);
      // Don't trigger validation for new items - let them validate onBlur or on save
      return;
    } else if (action.type === "remove") {
      spendInputFields.remove(action.index);
    } else if (action.type === "update") {
      spendInputFields.update(action.index, action.value);
    }

    // Let the form handle validation naturally through onBlur behavior
    // Only trigger validation on save/submit, not on programmatic updates

    // Reset the form to un-dirty
    // spendInputForm.reset();
  };

  const handleRemoveSpendInputBusinessUnit = async (bcp: BCPNode) => {
    const rows = spendInputForm.getValues("spendInputs");
    const newRows = rows.filter(
      (row) =>
        row.brand !== bcp.brand || row.category !== bcp.category || row.product !== bcp.product
    );
    spendInputForm.setValue("spendInputs", newRows);

    const scenario = formsToScenario();

    await updateScenario.mutateAsync({
      scenarioUUID: uuid,
      scenario,
      scenarioType,
    });

    await spendInputForm.trigger();

    // Reset the form to un-dirty
    spendInputForm.reset({
      spendInputs: newRows,
    });
  };

  const handleResetConstraints = async () => {
    spendInputForm.reset({
      spendInputs: [],
    });
    mediaConstraintsForm.reset({
      mediaConstraints: [],
    });

    scenarioInformationForm.setValue("spendGeographicGranularity", null);
    scenarioInformationForm.resetField("spendGeographicGranularity");

    const scenario = formsToScenario();

    const updatedScenario = await updateScenario.mutateAsync({
      scenarioUUID: uuid,
      scenario,
      scenarioType,
    });

    syncForm(updatedScenario);
  };

  const saveForm = async () => {
    const scenarioUuid = uuid;
    if (scenarioUuid === undefined) {
      throw new Error("Scenario UUID is missing");
    }

    // Validate forms before saving
    const isValid = await validateForms();
    if (!isValid) {
      toast.error(`Please fix all highlighted errors and try again.`);
      throw new Error("Form validation failed");
    }

    const scenarioValues = formsToScenario();

    try {
      const updatedForm = await updateScenario.mutateAsync({
        scenarioUUID: uuid,
        scenario: scenarioValues,
        scenarioType,
      });

      syncForm(updatedForm);

      await mediaConstraintsForm.trigger();
      await spendInputForm.trigger();
    } catch (error) {
      toast.error(`Please fix all highlighted errors and try again.`);

      throw error;
    }
  };

  const runScenario = useRunScenario({ isScenariosApiEnabled });

  const { navigateToScenariosList } = useScenariosV3Routes();

  const handleRunScenario = async () => {
    if (scenario === undefined || scenario.uuid === undefined) return;

    if (isScenariosApiEnabled) {
      const isValid = await validateForms();
      if (!isValid) {
        toast.error(`Please fix all highlighted errors and try again.`);
        return;
      }

      await runScenario.mutateAsync({
        scenarioId: scenario.uuid,
      });

      if (scenario.scenarioType === ScenarioTypeEnum.Optimizer) {
        trackScenarioSubmitted({
          mediaSpendConstraints: mediaConstraintsForm.getValues().mediaConstraints.length,
        });
      } else if (scenario.scenarioType === ScenarioTypeEnum.Budget) {
        trackScenarioSubmitted({
          mediaSpendInputs: spendInputForm.getValues().spendInputs.length,
        });
      }

      navigateToScenariosList();
      return;
    }

    if (scenario.scenarioType === ScenarioTypeEnum.Optimizer) {
      const isValid = await validateForms();
      if (!isValid) {
        toast.error(`Please fix all highlighted errors and try again.`);
        return;
      }

      const runScenarioRequest: RunScenarioRequest = {
        type: "optimizer",
        optimizerMediaConstraints: mediaConstraintsForm
          .getValues()
          .mediaConstraints.map((constraint) => ({
            geography: constraint.geography,
            maxValue: constraint.maxValue,
            minValue: constraint.minValue,
            channel: constraint.channel,
            product: constraint.product,
            category: constraint.category,
            brand: constraint.brand,
          })),
      };

      await runScenario.mutateAsync({
        scenarioId: scenario.uuid,
        runScenarioRequest,
      });

      // Track scenario submission for Optimizer
      trackScenarioSubmitted({
        mediaSpendConstraints: mediaConstraintsForm.getValues().mediaConstraints.length,
      });

      navigateToScenariosList();
    }

    if (!budgetAllocation) {
      const errorMessage = `Budget allocation failed - no budget allocation available`;
      toast.error(`Please fix all highlighted errors and try again.`);
      trackScenarioFormError(errorMessage);
      return;
    }

    if (scenario.scenarioType === ScenarioTypeEnum.Budget) {
      const isValid = await validateForms();
      if (!isValid) {
        toast.error(`Please fix all highlighted errors and try again.`);
        return;
      }

      const runScenarioRequest: RunScenarioRequest = {
        type: "budget",
        allocatedSpends: budgetAllocation?.map((constraint) => ({
          geography: constraint.geography,
          spend: constraint.spend,
          channel: constraint.channel,
          product: constraint.product,
          category: constraint.category,
          brand: constraint.brand,
        })),
      };

      await runScenario.mutateAsync({
        scenarioId: scenario.uuid,
        runScenarioRequest,
      });

      // Track scenario submission for Forecast
      trackScenarioSubmitted({
        mediaSpendInputs: spendInputForm.getValues().spendInputs.length,
      });

      navigateToScenariosList();
    }
  };

  const getConstraintsWithMaxSpend = (constraints: SpendConstraintsResponse["constraints"]) => {
    return constraints?.filter((constraint) => constraint?.maxSpend > 0) ?? [];
  };

  const getActiveChannelSpendNodes = (historicalSpends: HistoricalSpendDTO[]) => {
    return (
      historicalSpends
        ?.filter((spend) => spend.is_node_channel_active && spend.marketing_spend.spend > 0)
        ?.map((spend) => spend.marketing_spend.marketing_spend_node) ?? []
    );
  };

  // Generate distinct BCPs from constraints or historical spend data
  const distinctBCP = useMemo(() => {
    const constraintsWithSpend = isScenarioBuilderConstraintsFromApiEnabled
      ? getConstraintsWithMaxSpend(spendConstraintsDTO?.constraints ?? [])
      : getActiveChannelSpendNodes(historicalSpendsDTO ?? []);

    return constraintsWithSpend?.reduce((acc, constraint) => {
      const key = bcpLayerKey(constraint);
      if (!acc.has(key)) {
        acc.set(key, {
          brand: constraint.brand,
          category: constraint.category,
          product: constraint.product,
        });
      }
      return acc;
    }, new Map<string, BCPNode>());
  }, [historicalSpendsDTO, isScenarioBuilderConstraintsFromApiEnabled, spendConstraintsDTO]);

  return (
    <ScenarioFormContext.Provider
      value={{
        scenarioInformationForm,
        geoStatus,
        handleScenarioInformationChange,
        handleSpendGeographicGranularityChange,
        budgetAllocationForm,
        handleTotalBudgetChange,
        mediaConstraintsForm,
        handleMediaConstraintsRowChange,
        handleRemoveMediaConstraintsBusinessUnit,
        spendInputForm,
        handleSpendInputRowChange,
        handleRemoveSpendInputBusinessUnit,
        handleResetConstraints,
        brandGeographies: metaData?.brand_geographies,
        scenario,
        allowedDateRange: dateRanges,
        historicalSpends,
        historicalSpendsDTO,
        areHistoricalSpendsLoading,
        maxAllowedTotalBudget,
        loading: isScenarioLoading || isBrandGeographiesLoading,
        budgetAllocation,
        validateForms,
        saveForm,
        isSaving,
        handleRunScenario,
        isSubmitting: runScenario.isPending,
        distinctBCP,
        spendConstraintsDTO,
        isSpendConstraintsLoading,
      }}
    >
      {children}
    </ScenarioFormContext.Provider>
  );
};

export const useScenarioForms = () => {
  const context = useContext(ScenarioFormContext);
  if (!context) {
    throw new Error("useScenarioForms must be used within a ScenarioFormProvider");
  }
  return context;
};
